import { LitElement, html, css, unsafeCSS } from 'lit';
import styles from './tooltip.scss?inline';
import {
    TOOLTIP_ADAPTIVE_ATTR,
    TOOLTIP_ATTR,
    TOOLTIP_HOVERED_ATTR,
    TOOLTIP_NOWRAP_ATTR,
    TOOLTIP_TYPE_ATTR,
} from '@/components/tooltip/constants';
import { getScrollbarWidth } from '@/helpers/fixedBody.js';

/**
 * @attribute data-tooltip-adaptive - For an element with this attribute, tooltip will only
 * be displayed if the element's text content overflows its container
 */
export default class SotbitTooltip extends LitElement {
    static styles = css`
        ${unsafeCSS(styles)}
    `;

    static properties = {
        hidden: { type: Boolean, reflect: true },
        nowrap: { type: Boolean, reflect: true },
        hovered: { type: Boolean, reflect: true },
        adaptive: { type: Boolean, reflect: true },
    };

    constructor() {
        super();

        this.timeoutId = null;
        this.timeoutDuration = 400;
        this.hidden = true;
        this.nowrap = false;
        this.hovered = false;
        this.adaptive = false;
        this._handleMouseMoveBind = this._handleMouseMove.bind(this);
        this._pendingPositionUpdate = false;
        this._currentTarget = null;
    }

    connectedCallback() {
        super.connectedCallback();

        this.setAttribute('connected', '');
        document.addEventListener('mousemove', this._handleMouseMoveBind);
    }

    disconnectedCallback() {
        super.disconnectedCallback();

        this.removeAttribute('connected');
        document.removeEventListener('mousemove', this._handleMouseMoveBind);
    }

    updated(changedProperties) {
        super.updated(changedProperties);

        if (changedProperties.has('hidden') && !this.hidden && this._pendingPositionUpdate) {
            this._adjustPosition();
            this._pendingPositionUpdate = false;
        }
    }

    _handleMouseMove(e) {
        const target = e.target;

        if (!(target instanceof Element)) {
            return;
        }

        const ttRect = this.getBoundingClientRect();
        const tooltipTarget = target.closest(`[${TOOLTIP_ATTR}]`);

        if (!tooltipTarget) {
            this._currentTarget = tooltipTarget;
            this.hide();
            return;
        }

        if (tooltipTarget !== this._currentTarget) {
            this._currentTarget = tooltipTarget;
            this.hide();
            return;
        }

        this.nowrap = tooltipTarget.hasAttribute(TOOLTIP_NOWRAP_ATTR);
        this.hovered = tooltipTarget.hasAttribute(TOOLTIP_HOVERED_ATTR);
        this.adaptive = tooltipTarget.hasAttribute(TOOLTIP_ADAPTIVE_ATTR);

        if (this.adaptive) {
            if (
                this._currentTarget.scrollWidth === this._currentTarget.offsetWidth &&
                this._currentTarget.scrollHeight === this._currentTarget.offsetHeight
            ) {
                return;
            }
        }

        if (this.hovered) {
            const isOverComponent =
                e.clientX >= ttRect.left &&
                e.clientX <= ttRect.right &&
                e.clientY >= ttRect.top &&
                e.clientY <= ttRect.bottom;

            if (isOverComponent) {
                return;
            }
        }

        if (this.timeoutId) {
            clearTimeout(this.timeoutId);
            this.timeoutId = null;
        }

        if (!this.hidden) {
            return;
        }

        const content = tooltipTarget.getAttribute(TOOLTIP_ATTR)?.trim();
        if (!content) {
            this.hide();
            return;
        }

        this.innerHTML = '';

        if (tooltipTarget.getAttribute(TOOLTIP_TYPE_ATTR) === 'html') {
            this.insertAdjacentHTML('beforeend', content);
        } else {
            this.textContent = content;
        }

        this._pendingMouseEvent = e;
        this._pendingPositionUpdate = true;

        this.timeoutId = setTimeout(() => {
            this.hidden = false;
        }, this.timeoutDuration);
    }

    _adjustPosition() {
        if (!this._pendingMouseEvent) {
            return;
        }

        const e = this._pendingMouseEvent;
        const ttRect = this.getBoundingClientRect();
        const xOffset = 4;
        const yOffset = 12;

        let left = e.clientX + xOffset;
        let top = e.clientY + yOffset;

        const { scrollbarWidth, hasScrollbar } = getScrollbarWidth();
        const windowVisibleInnerWidth = hasScrollbar ? window.innerWidth - scrollbarWidth : window.innerWidth;

        if (left + ttRect.width > windowVisibleInnerWidth) {
            left = Math.max(0, windowVisibleInnerWidth - ttRect.width - xOffset);
        }

        if (top + ttRect.height > window.innerHeight) {
            top = Math.max(0, e.clientY - ttRect.height - yOffset);
        }

        if (top + ttRect.height > window.innerHeight) {
            top = window.innerHeight - ttRect.height - yOffset;
        }

        this.style.left = `${left + window.scrollX}px`;
        this.style.top = `${top + window.scrollY}px`;
    }

    hide() {
        clearTimeout(this.timeoutId);
        this.timeoutId = null;
        this.hidden = true;
        this._pendingMouseEvent = null;
        this._pendingPositionUpdate = false;
    }

    show({ target, message, duration, topOffset }) {
        if (!message?.trim()?.length) {
            return;
        }

        if (!duration) {
            duration = this.timeoutDuration;
        }

        if (!topOffset) {
            topOffset = 0;
        }

        this.innerHTML = '';
        this.textContent = message;
        this.hidden = false;

        const rect = target.getBoundingClientRect();
        const ttRect = this.getBoundingClientRect();

        let left = rect.right - ttRect.width;
        let top = rect.bottom + topOffset;

        if (left < 0) {
            left = 0;
        }

        if (left + ttRect.width > window.innerWidth) {
            left = window.innerWidth - ttRect.width;
        }

        if (top + ttRect.height > window.innerHeight) {
            top = rect.top - ttRect.height - topOffset;
        }

        this.style.left = `${left + window.scrollX}px`;
        this.style.top = `${top + window.scrollY}px`;

        setTimeout(() => {
            this.hidden = true;
        }, duration);
    }

    render() {
        return html` <slot></slot>`;
    }
}
